/**
 * Sign-in through BlueSphere Identity, identity-client's way (Story 53,
 * design r28 "One sign-in across BlueSphere").
 *
 * The console holds no sign-in logic of its own. `@bluesphere/identity-client`
 * gives the AuthProvider's settings (`oidcSettings`), the screen
 * (`SignInGate`: a silent sign-in on managed login's session first, then the
 * work-email screen with `idp_identifier`) and sign-out (`signOut`: the
 * session in every app, then `/logout`). This file only builds its config.
 *
 * Values come from Vite env vars at build time (see `.env.example`), which
 * `scripts/deploy-web.sh` reads from the integrations-api stack's outputs.
 * `mdr-web` registers the SPA's URLs with a trailing slash, which is the
 * redirect identity-client sends (`<origin>/`).
 *
 * MDR's own Cognito pool has no sign-in path here (open question 29). The
 * API still accepts its tokens until task 219.
 */

import type { IdentityConfig } from '@bluesphere/identity-client';

const PUBLIC_URL =
  import.meta.env.VITE_PUBLIC_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export const identityConfig: IdentityConfig = {
  issuer: import.meta.env.VITE_IDENTITY_ISSUER ?? '',
  clientId: import.meta.env.VITE_IDENTITY_CLIENT_ID ?? '',
  loginDomain: import.meta.env.VITE_IDENTITY_DOMAIN ?? '',
  scopes: ['mdr/api'],
  // POST /sign-out, which ends the session in every app (design r28).
  identityApi: import.meta.env.VITE_IDENTITY_API || undefined,
};

if (!identityConfig.issuer || !identityConfig.clientId || !identityConfig.loginDomain) {
  // Surface missing config loudly in dev; deploy-web.sh refuses to build
  // without these.
  console.warn(
    '[auth] Missing Identity config. Set VITE_IDENTITY_ISSUER, ' +
      'VITE_IDENTITY_CLIENT_ID and VITE_IDENTITY_DOMAIN.',
  );
}
if (!identityConfig.identityApi) {
  console.warn('[auth] VITE_IDENTITY_API is not set: sign-out ends this app and managed login only.');
}

/**
 * Tokens from before Story 53 lived in `localStorage` (both pools, as
 * `oidc.user:<authority>:<client>`), with the chosen pool and the return path
 * beside them. identity-client keeps tokens in memory, so none of these is
 * read again; remove them so no token outlives the change.
 *
 * Not the other `oidc.*` keys: oidc-client-ts keeps a sign-in's flow state
 * there (`oidc.<state id>`) across the redirect, and the callback needs it.
 */
export function clearLegacyStorage(): void {
  try {
    const store = window.localStorage;
    const stale: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && (key.startsWith('oidc.user:') || key === 'bsmdr.issuer')) stale.push(key);
    }
    for (const key of stale) store.removeItem(key);
    window.sessionStorage.removeItem('bsmdr.returnTo');
  } catch {
    /* storage unavailable: nothing was stored either */
  }
}

/**
 * The console's own address: what an Admin copies to a newly invited user
 * (Story 22). There is no invitation email in the MVP (task 118); signing in
 * here with the invited account binds the record.
 */
export function signInUrl(): string {
  return PUBLIC_URL;
}
